import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { useMeetingVideoUpload } from '@/hooks/use-meeting-video-upload.tsx';
import '@/assets/styles/components/video-upload-section.styles.scss';

interface VideoUploadSectionProps {
	interactionId: string;
	externalReportId: number;
	onUploadSuccess?: () => void;
	externalMeetingReport: any;
}

const VideoUploadSection: React.FC<VideoUploadSectionProps> = ({ interactionId, externalReportId, onUploadSuccess, externalMeetingReport }) => {
	const { getInfoAndUploadToS3, uploadState } = useMeetingVideoUpload({
		interactionId
	});

	const [isDragging, setIsDragging] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const hasExistingVideo = Boolean(externalMeetingReport?.recordingFileName);

	const handleFileSelect = async (file: File) => {
		if (!file.type.startsWith('video/')) {
			return;
		}

		try {
			const result = await getInfoAndUploadToS3(file, externalReportId);
			if (result) {
				onUploadSuccess?.();
			}
		} catch (error) {
			console.error('Upload error:', error);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const handleDragEnter = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		const file = e.dataTransfer.files[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const handleClick = () => {
		fileInputRef.current?.click();
	};

	return (
		<div className="video-upload-section">
			<div className="video-upload-section__header">
				<div className="video-upload-section__icon">
					<i className="icons-chat-round-video" />
				</div>
				<div className="video-upload-section__content">
					<div className="video-upload-section__title">Görüşme Kaydı Yükleme</div>
					<div className="video-upload-section__description">Görüşme kaydınızı Drive'a yükleyemediyseniz, buradan doğrudan yükleyebilirsiniz.</div>
				</div>
			</div>

			<div
				className={clsx('video-upload-section__dropzone', {
					'is-dragging': isDragging,
					'is-uploading': uploadState.isUploading,
					'is-success': uploadState.success || hasExistingVideo,
					'is-error': uploadState.error
				})}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				onClick={handleClick}>
				<input
					ref={fileInputRef}
					type="file"
					accept="video/*"
					onChange={handleFileChange}
					style={{ display: 'none' }}
				/>

				{uploadState.isUploading ? (
					<div className="video-upload-section__uploading">
						<div className="video-upload-section__spinner">
							<div className="spinner"></div>
						</div>
						<div className="video-upload-section__upload-text">Video yükleniyor...</div>
						<div className="video-upload-section__progress-bar">
							<div
								className="video-upload-section__progress-fill"
								style={{ width: `${uploadState.progress}%` }}
							/>
						</div>
						<div className="video-upload-section__progress-text">%{uploadState.progress}</div>
					</div>
				) : uploadState.success || hasExistingVideo ? (
					<div className="video-upload-section__success">
						<i className="icons-check-circle" />
						<div className="video-upload-section__success-text">Video başarıyla yüklendi!</div>

						<button
							className="video-upload-section__upload-again"
							onClick={e => {
								e.stopPropagation();
								handleClick();
							}}>
							Başka bir video yükle
						</button>
					</div>
				) : uploadState.error ? (
					<div className="video-upload-section__error-state">
						<i className="icons-close-circle" />
						<div className="video-upload-section__error-text">{uploadState.error}</div>
						<button
							className="video-upload-section__retry"
							onClick={e => {
								e.stopPropagation();
								handleClick();
							}}>
							Tekrar dene
						</button>
					</div>
				) : (
					<div className="video-upload-section__idle">
						<div className="video-upload-section__upload-icon">
							<i className="icons-videocamera" />
						</div>
						<div className="video-upload-section__idle-title">Video dosyanızı sürükleyip bırakın</div>
						<div className="video-upload-section__idle-subtitle">veya bilgisayarınızdan seçin</div>
						<div className="video-upload-section__file-types">Desteklenen formatlar: MP4, MOV, AVI, WebM</div>
					</div>
				)}
			</div>

			{uploadState.error && !uploadState.isUploading && (
				<div className="video-upload-section__error-message">
					<i className="icons-info-circle" />
					{uploadState.error}
				</div>
			)}

			<div className="video-upload-section__info">
				<i className="icons-info-circle" />
				<span>
					<b>Önemli:</b> Görüşme kaydı yüklenmediği takdirde ücret ödemesi yapılmaz. Lütfen videonuzu mutlaka yükleyin.
				</span>
			</div>
		</div>
	);
};

export default VideoUploadSection;
